import React from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { Download, Loader2, AlertTriangle } from "lucide-react";
import { fadeUp, stagger } from "../lib/motions";

export default function Gallery() {
  const { data, isLoading } = useQuery({
    queryKey: ["jobs"],
    queryFn: async () => (await api.get("/jobs")).data,
    refetchInterval: 15000,
  });
  const jobs = data?.jobs || [];

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 md:py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.24em] text-cyan-400 mb-1">Gallery</div>
          <h1 data-testid="gallery-heading" className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">Your generations</h1>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-14 rounded-3xl border border-dashed border-white/10 text-center">
            <div className="text-zinc-400 text-lg mb-1">No videos yet</div>
            <div className="text-zinc-500 text-sm">Head to the Studio to create your first cinematic clip.</div>
          </div>
        ) : (
          <motion.div initial="hidden" animate="show" variants={stagger(0.05, 0.06)}
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {jobs.map((j) => (
              <motion.div key={j.id} variants={fadeUp} whileHover={{ y: -3 }} transition={{ duration: 0.2 }}
                          data-testid={`gallery-item-${j.id}`} className="rounded-2xl overflow-hidden bg-white/[0.02] border border-white/10 hover:border-white/25 transition-colors">
                <div className="aspect-video bg-black relative">
                  {j.status === "succeeded" && j.video_url ? (
                    <video src={j.video_url} controls playsInline className="w-full h-full object-contain" />
                  ) : j.status === "failed" ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-red-400 text-sm p-4">
                      <AlertTriangle className="w-5 h-5 mb-2" />
                      <div className="text-center">Generation failed</div>
                      <div className="text-[10px] text-zinc-500 mt-1 text-center line-clamp-2">{j.error}</div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-500">
                      <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{j.model_name}</div>
                    <div className="text-[10px] font-mono text-zinc-400">{j.resolution} · {j.duration}s</div>
                  </div>
                  <p className="text-sm text-zinc-300 line-clamp-2">{j.prompt}</p>
                  {j.video_url && (
                    <a href={j.video_url} download data-testid={`download-${j.id}`}
                       className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 mt-3">
                      <Download className="w-3.5 h-3.5" /> Download
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </AppShell>
  );
}
